// Chart data builders shared by the report viewer, the config preview and the dashboard widgets.
// They emit the shapes `@chatwoot/viz` reads: `{ categories, series }` for bar and line charts,
// `{ segments }` for the pie (donut) one. To let a click drill into the events behind it, every
// category also has an entry in `buckets` (its period and/or dimension value), and a series or
// segment that plots one dimension value carries it as `dimensionValue`.

// Theme tokens rather than literal colours, so the series stay legible in both light and dark mode.
const CHART_COLORS = [
  'rgb(var(--blue-9))',
  'rgb(var(--teal-9))',
  'rgb(var(--amber-9))',
  'rgb(var(--ruby-9))',
  'rgb(var(--violet-9))',
  'rgb(var(--iris-9))',
];

export function chartColorAt(index) {
  return CHART_COLORS[index % CHART_COLORS.length];
}

const TIME_RANGE_DAYS = {
  last_7_days: 7,
  last_30_days: 30,
  last_90_days: 90,
};

// Only additive aggregations can be shown as shares of a total; a percentage of a
// summed average (or min/max) is arithmetically meaningless.
const SHAREABLE_AGGREGATIONS = ['count', 'sum'];

function isShareable(metric) {
  return SHAREABLE_AGGREGATIONS.includes(metric?.aggregation);
}

export function formatPercentage(value) {
  const num = Number(value) || 0;
  return `${num.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

export function formatMetricValue(value) {
  if (value == null) return '—';
  const num = Number(value);
  if (Number.isInteger(num)) return num.toLocaleString();
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

// Denominators for percentage table cells, chosen to match what the chart shows:
// dimensioned time-bucketed data stacks each period to 100%, so a cell shares
// against its period total; otherwise it shares against its column total. Returns
// a lookup `(row, metricKey) => total | null` — null for metrics whose aggregation
// cannot be expressed as a share (avg/min/max), which then render as absolute.
export function buildTableCellTotals(rows, metrics, perPeriod = false) {
  const totals = {};
  (metrics || []).forEach(metric => {
    if (!isShareable(metric)) return;
    (rows || []).forEach(row => {
      const bucket = perPeriod ? `${row.period}|${metric.key}` : metric.key;
      totals[bucket] = (totals[bucket] || 0) + (Number(row[metric.key]) || 0);
    });
  });
  return (row, metricKey) => {
    const bucket = perPeriod ? `${row.period}|${metricKey}` : metricKey;
    return bucket in totals ? totals[bucket] : null;
  };
}

// Render a table cell honouring value mode: percentage shows the cell's share of
// the given total; absolute (or a null total — non-shareable metric) shows the raw
// value. A missing value or an all-zero total has no defined share, so render the
// same placeholder absolute mode uses rather than asserting "0%".
export function formatTableCell(value, valueMode, total) {
  if (valueMode === 'percentage' && total != null) {
    if (value == null || !total) return '—';
    return formatPercentage((Number(value) / total) * 100);
  }
  return formatMetricValue(value);
}

// Display text for a dimension value: the record name the query resolved for it (agents,
// contacts, ...), otherwise the value itself, or `emptyLabel` for events without a value.
export function buildDimensionLabel(labels = {}, emptyLabel = 'Unknown') {
  return value =>
    value == null || value === ''
      ? emptyLabel
      : (labels[value] ?? String(value));
}

const defaultDimensionLabel = buildDimensionLabel();

export function formatPeriodLabel(period, granularity) {
  const date = new Date(period);
  if (granularity === 'year') {
    return date.toLocaleDateString(undefined, { year: 'numeric' });
  }
  if (granularity === 'month') {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
    });
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

// Resolve a period selection to absolute { since, until } ISO strings. Explicit
// since/until (e.g. a custom range) win; otherwise fall back to the preset window.
export function resolvePeriodRange({ timeRange, since, until } = {}) {
  if (since && until) return { since, until };
  const days = TIME_RANGE_DAYS[timeRange] ?? 30;
  const now = new Date();
  return {
    since: new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString(),
    until: now.toISOString(),
  };
}

export function buildQueryParamsFor({
  timeRange,
  granularity,
  dimension,
  since,
  until,
}) {
  const range = resolvePeriodRange({ timeRange, since, until });

  const params = {
    since: range.since,
    until: range.until,
    time_granularity: granularity,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  if (dimension) {
    params.dimension = dimension;
  }

  return params;
}

function buildSimpleChartData(timeseries, metrics, granularity) {
  return {
    categories: timeseries.map(row =>
      formatPeriodLabel(row.period, granularity)
    ),
    buckets: timeseries.map(row => ({ period: row.period })),
    series: metrics.map((metric, index) => ({
      id: metric.key,
      label: metric.label || metric.key,
      color: chartColorAt(index),
      data: timeseries.map(row => row[metric.key] || 0),
    })),
  };
}

function buildDimensionedChartData(
  timeseries,
  metrics,
  granularity,
  dimensionLabel
) {
  const periodsSet = new Set();
  const dimensionValues = new Set();
  const dataMap = {};

  timeseries.forEach(row => {
    const period = row.period;
    const dimVal = row.dimension_value ?? null;
    periodsSet.add(period);
    dimensionValues.add(dimVal);
    if (!dataMap[period]) dataMap[period] = {};
    dataMap[period][dimVal] = row;
  });

  const periods = [...periodsSet].sort();
  const dims = [...dimensionValues].sort();
  const metric = metrics[0];

  return {
    categories: periods.map(period => formatPeriodLabel(period, granularity)),
    buckets: periods.map(period => ({ period })),
    series: dims.map((dimVal, index) => ({
      id: String(dimVal),
      label: dimensionLabel(dimVal),
      dimensionValue: dimVal,
      color: chartColorAt(index),
      data: periods.map(period => dataMap[period]?.[dimVal]?.[metric.key] || 0),
    })),
  };
}

export function buildSegments(entries) {
  return entries.map(([label, value], index) => ({
    id: label,
    label,
    value,
    color: chartColorAt(index),
  }));
}

// Flat (no time bucketing) chart data. The timeseries carries no period, so bars
// are keyed by dimension value — or, with no dimension, by metric.
function buildFlatChartData(timeseries, metrics, hasDimension, dimensionLabel) {
  if (hasDimension) {
    return {
      categories: timeseries.map(row => dimensionLabel(row.dimension_value)),
      buckets: timeseries.map(row => ({
        dimensionValue: row.dimension_value ?? null,
      })),
      series: metrics.map((metric, index) => ({
        id: metric.key,
        label: metric.label || metric.key,
        color: chartColorAt(index),
        data: timeseries.map(row => row[metric.key] || 0),
      })),
    };
  }

  // One bar per metric. The category labels already name each bar, so a single
  // series (one colour) reads better here than colouring what the axis says.
  const row = timeseries[0] || {};
  return {
    categories: metrics.map(metric => metric.label || metric.key),
    buckets: metrics.map(() => ({})),
    series: [
      {
        id: 'value',
        label: '',
        color: chartColorAt(0),
        data: metrics.map(metric => row[metric.key] || 0),
      },
    ],
  };
}

function buildPieChartData(timeseries, metrics, hasDimension, dimensionLabel) {
  const primaryMetric = metrics[0];
  if (!primaryMetric) return null;

  if (hasDimension) {
    const totals = new Map();
    timeseries.forEach(row => {
      const key = row.dimension_value ?? null;
      totals.set(key, (totals.get(key) || 0) + (row[primaryMetric.key] || 0));
    });
    return {
      segments: [...totals].map(([dimensionValue, value], index) => ({
        id: String(dimensionValue),
        label: dimensionLabel(dimensionValue),
        value,
        color: chartColorAt(index),
        dimensionValue,
      })),
    };
  }

  return {
    segments: buildSegments(
      metrics.map(metric => [
        metric.label || metric.key,
        timeseries.reduce((sum, row) => sum + (row[metric.key] || 0), 0),
      ])
    ),
  };
}

// Convert absolute dataset values into percentages.
// - Dimensioned charts normalise column-wise (each period's bars sum to 100%),
//   which renders as a 100% stacked chart.
// - Non-dimensioned charts normalise each series against its own total, so a
//   bar shows what share of the range that period represents.
function toPercentageData({ categories, series, ...rest }, stackedByCategory) {
  if (stackedByCategory) {
    const categoryTotals = categories.map((_, index) =>
      series.reduce((sum, one) => sum + (one.data[index] || 0), 0)
    );
    return {
      ...rest,
      categories,
      series: series.map(one => ({
        ...one,
        data: one.data.map((value, index) =>
          categoryTotals[index] ? (value / categoryTotals[index]) * 100 : 0
        ),
      })),
    };
  }

  return {
    ...rest,
    categories,
    series: series.map(one => {
      const total = one.data.reduce((sum, value) => sum + (value || 0), 0);
      return {
        ...one,
        data: one.data.map(value => (total ? (value / total) * 100 : 0)),
      };
    }),
  };
}

export function buildChartData({
  timeseries,
  metrics,
  granularity,
  hasDimension,
  visualization,
  valueMode = 'absolute',
  dimensionLabel = defaultDimensionLabel,
}) {
  if (!timeseries.length) return null;
  // Pie slices are inherently proportional, so percentage mode only changes how
  // the tooltip is formatted — the underlying values stay absolute.
  if (visualization === 'pie_chart') {
    return buildPieChartData(timeseries, metrics, hasDimension, dimensionLabel);
  }

  const isFlat = granularity === 'none';

  let data;
  if (isFlat) {
    data = buildFlatChartData(
      timeseries,
      metrics,
      hasDimension,
      dimensionLabel
    );
  } else if (hasDimension) {
    data = buildDimensionedChartData(
      timeseries,
      metrics,
      granularity,
      dimensionLabel
    );
  } else {
    data = buildSimpleChartData(timeseries, metrics, granularity);
  }

  // Dimensioned time-bucketed charts plot only the first metric; the others plot
  // every metric as its own series. Normalising is skipped entirely unless all
  // plotted metrics are shareable — mixing % and absolute series on one axis
  // would be worse than staying absolute.
  const plottedMetrics = hasDimension && !isFlat ? [metrics[0]] : metrics;
  if (valueMode !== 'percentage' || !plottedMetrics.every(isShareable)) {
    return data;
  }

  // Without a time axis there are no per-period columns to stack, so each bar is
  // normalised against its series total instead — dimension values become shares
  // of the overall total (summing to 100% across the chart).
  return toPercentageData(data, hasDimension && !isFlat);
}

// The viz charts take their presentation as props rather than a chart.js options object,
// so value mode resolves to what `ReportChart` forwards to BarChart/LineChart. The donut
// needs nothing here: it labels every slice with its share already.
export function buildChartProps({
  valueMode = 'absolute',
  hasDimension = false,
  granularity,
  metrics = [],
} = {}) {
  // Must mirror buildChartData: percentage presentation only when the plotted
  // metrics were actually normalised.
  const isFlat = granularity === 'none';
  const plottedMetrics = hasDimension && !isFlat ? [metrics[0]] : metrics;
  const isPercentage =
    valueMode === 'percentage' &&
    plottedMetrics.length > 0 &&
    plottedMetrics.every(isShareable);
  // A flat chart has no per-period columns to stack; its percentage bars are independent
  // shares of the total, so they must not be stacked.
  const stacked = isPercentage && hasDimension && granularity !== 'none';

  return {
    stacked,
    // Cap the axis at 100 only for 100% stacked charts; a per-series percentage
    // breakdown uses smaller values and shouldn't be squashed against the top.
    yDomain: stacked ? [0, 100] : undefined,
    formatValue: isPercentage ? formatPercentage : formatMetricValue,
  };
}

// A donut refuses to draw an all-zero dataset, so emptiness has to be judged on the values rather
// than on the number of slices.
export function hasChartData(chartData) {
  if (!chartData) return false;
  if (chartData.segments) {
    return chartData.segments.some(segment => segment.value > 0);
  }
  return chartData.categories.length > 0;
}

function pickDimensionValue(source) {
  return source && 'dimensionValue' in source
    ? { dimensionValue: source.dimensionValue }
    : {};
}

// The bucket a chart click points at, as `{ period?, dimensionValue? }`. A present
// `dimensionValue` (null for events without a value) narrows the drilldown to that group.
export function drilldownBucketForClick(chartData, click) {
  if (chartData.segments) return pickDimensionValue(click.item);

  return {
    ...chartData.buckets[click.categoryIndex],
    ...pickDimensionValue(click.series),
  };
}

// The bucket a table row aggregates: its period when bucketed by time, its dimension value
// when grouped.
export function drilldownBucketForRow(row, { timeBucketed, hasDimension }) {
  return {
    ...(timeBucketed ? { period: row.period } : {}),
    ...(hasDimension ? { dimensionValue: row.dimension_value ?? null } : {}),
  };
}

export function describeDrilldownBucket(bucket, granularity, dimensionLabel) {
  return [
    bucket.period && formatPeriodLabel(bucket.period, granularity),
    'dimensionValue' in bucket && dimensionLabel(bucket.dimensionValue),
  ]
    .filter(Boolean)
    .join(' ⋅ ');
}

// Drilldown params: the exact query the bucket came from, narrowed to that bucket. The
// "no value" group is sent without a dimension_value, which the API reads as "no value".
export function buildDrilldownParams(queryParams, bucket) {
  return {
    ...queryParams,
    ...(bucket.period ? { period: bucket.period } : {}),
    ...(bucket.dimensionValue != null
      ? { dimension_value: bucket.dimensionValue }
      : {}),
  };
}
